import React, { useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useLazyQuery } from "@apollo/client";
import { PRODUCT_ID_QUERY } from "../../../../apis/taxonomy/queries";
import { ProductsQueryPayload } from "../../../../types/taxonomy";
import { UploadDialogProps, UploadStateData } from "../types";
import { TEXT, CSV_TEMPLATE_CONTENT, MAX_PRODUCTS_LIMIT } from "../constants";
import { parseProductCsv } from "./utils";
import { IdleContent } from "./IdleContent";
import { ProcessingContent } from "./ProcessingContent";
import { SuccessContent } from "./SuccessContent";
import { ErrorContent } from "./ErrorContent";
import { TAXONOMY_CLIENT } from "../../../../utils/consts";

export const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
  uploadProps,
}) => {
  const [uploadState, setUploadState] = useState<UploadStateData>({
    state: "idle",
    validProducts: [],
    notFoundProducts: [],
    errors: [],
    totalUploaded: 0,
  });

  const [fetchProducts] = useLazyQuery<ProductsQueryPayload, { ids: number[] }>(
    PRODUCT_ID_QUERY,
    {
      context: { clientName: TAXONOMY_CLIENT },
    }
  );

  // Handle file upload
  const handleFilesAccepted = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;

      const [file] = files;
      setUploadState({
        state: "processing",
        validProducts: [],
        notFoundProducts: [],
        errors: [],
        totalUploaded: 0,
      });

      try {
        // Parse CSV
        const effectiveLimit = uploadProps?.noProductsLimit
          ? undefined
          : MAX_PRODUCTS_LIMIT;
        const parseResult = await parseProductCsv(file, effectiveLimit);

        if (!parseResult.isValid) {
          setUploadState({
            state: "error",
            validProducts: [],
            notFoundProducts: [],
            errors: parseResult.errors,
            totalUploaded: 0,
          });
          return;
        }

        // Validate products against API
        const productIds = parseResult.data.map((row) =>
          Number(row.product_id)
        );
        const { data } = await fetchProducts({
          variables: { ids: productIds },
        });

        if (data?.product?.data) {
          const fetchedProductIds = new Set(
            data.product.data.map(({ id }) => id)
          );

          const validProducts = parseResult.data.filter((row) =>
            fetchedProductIds.has(Number(row.product_id))
          );

          const notFoundProducts = parseResult.data.filter(
            (row) => !fetchedProductIds.has(Number(row.product_id))
          );

          if (validProducts.length === 0) {
            setUploadState({
              state: "error",
              validProducts: [],
              notFoundProducts: [],
              errors: [TEXT.ERROR_NO_PRODUCTS_FOUND],
              totalUploaded: parseResult.data.length,
            });
          } else {
            setUploadState({
              state: "success",
              validProducts,
              notFoundProducts,
              errors: [],
              totalUploaded: parseResult.data.length,
            });
          }
        }
      } catch (error) {
        setUploadState({
          state: "error",
          validProducts: [],
          notFoundProducts: [],
          errors: [
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          ],
          totalUploaded: 0,
        });
      }
    },
    [fetchProducts, uploadProps?.noProductsLimit]
  );

  // Handle dialog close
  const handleClose = () => {
    setUploadState({
      state: "idle",
      validProducts: [],
      notFoundProducts: [],
      errors: [],
      totalUploaded: 0,
    });
    onClose();
  };

  // Handle import action
  const handleImport = () => {
    const productIds = uploadState.validProducts.map((p) =>
      Number(p.product_id)
    );
    onSuccess(productIds);
  };

  // Handle reset/try again
  const handleReset = () => {
    setUploadState({
      state: "idle",
      validProducts: [],
      notFoundProducts: [],
      errors: [],
      totalUploaded: 0,
    });
  };

  // Download template
  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE_CONTENT], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Render content based on state
  const renderContent = () => {
    switch (uploadState.state) {
      case "processing":
        return <ProcessingContent />;
      case "success":
        return (
          <SuccessContent
            validCount={uploadState.validProducts.length}
            notFoundProducts={uploadState.notFoundProducts}
          />
        );
      case "error":
        return <ErrorContent errors={uploadState.errors} />;
      default:
        return (
          <IdleContent
            onFilesAccepted={handleFilesAccepted}
            onDownloadTemplate={handleDownloadTemplate}
            customLimit={
              uploadProps?.noProductsLimit ? undefined : MAX_PRODUCTS_LIMIT
            }
          />
        );
    }
  };

  // Determine dialog actions based on state
  const renderActions = () => {
    switch (uploadState.state) {
      case "processing":
        return (
          <Button onClick={handleClose} color="secondary">
            {TEXT.DIALOG_CANCEL}
          </Button>
        );
      case "success":
        return (
          <>
            <Button onClick={handleReset} color="secondary">
              {TEXT.DIALOG_UPLOAD_ANOTHER}
            </Button>
            <Button onClick={handleImport} variant="contained" color="primary">
              {TEXT.DIALOG_IMPORT}
            </Button>
          </>
        );
      case "error":
        return (
          <>
            <Button onClick={handleClose} color="secondary">
              {TEXT.DIALOG_CANCEL}
            </Button>
            <Button onClick={handleReset} variant="contained" color="primary">
              {TEXT.DIALOG_TRY_AGAIN}
            </Button>
          </>
        );
      default:
        return (
          <Button onClick={handleClose} color="secondary">
            {TEXT.DIALOG_CANCEL}
          </Button>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { minHeight: 400 } } }}
    >
      <DialogTitle sx={{ m: 0, p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {uploadProps?.dialogTitle || TEXT.DIALOG_TITLE}
          </Typography>
          {uploadProps?.titleDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {uploadProps.titleDescription}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>{renderContent()}</DialogContent>

      <DialogActions sx={{ p: 2 }}>{renderActions()}</DialogActions>
    </Dialog>
  );
};
